import { Box, Heading, Skeleton, Stack } from '@chakra-ui/react';
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslations } from 'use-intl';

import { Exceptions } from '@blockpulse3/data/shared';
import {
  AddressInfosFragmentDoc,
  IdentityVerificationStatus,
  IndividualIdentityInfosFragmentDoc,
  UserRole,
  useGetUserByIndividualIdentityIdQuery,
  useUpdateAddressMutation,
  useUpdateIndividualIdentityMutation,
  useUpdatePasswordMutation,
} from '@blockpulse3/graphql/hooks';
import {
  LinkButton,
  NotAllowedCard,
  useErrorToast,
  useSuccessToast,
} from '@blockpulse3/ui/commons';

import { useUser } from '../../providers';
import { FiscalAccountFormView } from './FiscalAccount';
import { IUpdateAddressForm, UpdateAddressForm } from './UpdateAddressForm';
import { IUpdateEmailForm, UpdateEmailForm } from './UpdateEmailForm';
import { IUpdatePasswordForm, UpdatePasswordForm } from './UpdatePasswordForm';
import { UpdatePhoneForm } from './UpdatePhoneForm';
import { IUpdatePhoneForm } from './UpdatePhoneForm/types';
import { IUpdateProfileForm, UpdateProfileForm } from './UpdateProfileForm';
import { fmtAddress, fmtProfile } from './utils';

type Props = {
  backHref?: string;
};

/**
 * SettingsView.
 *
 * @returns {JSX.Element}
 */
export function UserSettingsView({ backHref }: Props): JSX.Element {
  const i18nSettings = useTranslations('Settings');
  const i18nGeneral = useTranslations('General');

  const errorToast = useErrorToast();
  const successToast = useSuccessToast();

  const { user: currentUser, loadUser } = useUser();
  const { individualIdentityId = '' } = useParams();

  const {
    data: individualUser,
    loading: loadingIndividualUser,
    refetch: refetchIndividualUser,
  } = useGetUserByIndividualIdentityIdQuery({
    variables: {
      individualIdentityId,
    },
    skip: !individualIdentityId,
  });

  const user = individualIdentityId ? individualUser?.getUserByIndividualIdentityId : currentUser;

  const refetch = (): void => {
    if (individualIdentityId) {
      refetchIndividualUser();
    } else {
      loadUser();
    }
  };

  /* ** Setting mutations ** */
  const [updatePassword] = useUpdatePasswordMutation();
  const [updateIndividualIdentity] = useUpdateIndividualIdentityMutation();
  const [updateIndividualIdentityAddress] = useUpdateAddressMutation();

  /* ** Build default values with util functions ** */
  const profileDefaults = user ? fmtProfile(user) : undefined;
  const addressDefaults = user ? fmtAddress(user) : undefined;
  const emailDefaults = { email: user?.email || '' };
  const phoneDefaults = { phone: user?.individualIdentity?.phone || '' };

  /* ** Prevent user from updating personal informations if kyc approved ** */
  const isVerified = useMemo(() => {
    return (
      user?.individualIdentity?.identity?.verificationStatus === IdentityVerificationStatus.APPROVED
    );
  }, [user?.individualIdentity?.identity]);

  if (
    individualIdentityId &&
    currentUser?.individualIdentity?.id !== individualIdentityId &&
    !currentUser?.roles.includes(UserRole.SUPERADMIN)
  ) {
    return <NotAllowedCard h="400px" />;
  }

  if (loadingIndividualUser) {
    return <Skeleton h="400px" />;
  }

  const handleProfileSubmit = (profileData: IUpdateProfileForm): void => {
    if (user?.individualIdentity) {
      updateIndividualIdentity({
        variables: {
          updateIndividualIdentityInput: {
            firstName: profileData.firstName,
            lastName: profileData.lastName,
            birthdate: profileData.birthdate,
            birthplace: profileData.birthplace,
            nationality: profileData.nationality.value,
            individualIdentityId: user.individualIdentity.id,
            gender: profileData.gender,
          },
        },
        onCompleted: () => {
          refetch();
          successToast({ title: i18nSettings('Toast.Success') });
        },
        onError: () => {
          errorToast({ title: i18nSettings('Toast.Error') });
        },
        update: (cache, { data }) => {
          /* ** Update IndividualIdentity cache entry for nested queries ** */
          if (user.individualIdentity && data?.updateIndividualIdentity) {
            const id = cache.identify(user?.individualIdentity);
            cache.writeFragment({
              id,
              fragment: IndividualIdentityInfosFragmentDoc,
              fragmentName: 'IndividualIdentityInfos',
              data: data.updateIndividualIdentity,
            });
          }
        },
      });
    }
  };

  const handleAddressSubmit = (addressData: IUpdateAddressForm): void => {
    if (user?.individualIdentity?.address) {
      updateIndividualIdentityAddress({
        variables: {
          updateAddressInput: {
            addressId: user?.individualIdentity.address?.id || '',
            line: addressData.line,
            city: addressData.city,
            postalCode: addressData.postalCode,
            country: addressData.country.value,
          },
        },
        onCompleted() {
          refetch();
          successToast({ title: i18nSettings('Toast.Success') });
        },
        onError() {
          errorToast({ title: i18nSettings('Toast.Error') });
        },
        update: (cache, { data }) => {
          /* ** Update Address cache entry for nested queries ** */
          if (user.individualIdentity?.address && data?.updateAddress) {
            const id = cache.identify(user.individualIdentity.address);
            cache.writeFragment({
              id,
              fragment: AddressInfosFragmentDoc,
              fragmentName: 'AddressInfos',
              data: data.updateAddress,
            });
          }
        },
      });
    }
  };

  const handlePasswordSubmit = (passwordData: IUpdatePasswordForm): void => {
    if (user) {
      updatePassword({
        variables: {
          updatePasswordInput: {
            userId: user.id,
            currentPassword: passwordData.currentPassword,
            newPassword: passwordData.newPassword,
          },
        },
        onCompleted: () => {
          successToast({ title: i18nSettings('Toast.Success') });
        },
        onError: () => {
          errorToast({ title: i18nSettings('Toast.Error') });
        },
      });
    }
  };

  const handleEmailSubmit = (emailData: IUpdateEmailForm): void => {
    if (user?.individualIdentity) {
      updateIndividualIdentity({
        variables: {
          updateIndividualIdentityInput: {
            individualIdentityId: user.individualIdentity.id,
            email: emailData.email,
          },
        },
        onCompleted: () => {
          successToast({ title: i18nSettings('Toast.Success') });
        },
        onError: (err: unknown) => {
          const error = err as Error;
          if (error.message === Exceptions.AlreadyUsedEmail) {
            errorToast({ title: i18nSettings('Toast.AlreadyUsedEmail') });
          } else {
            errorToast({ title: i18nSettings('Toast.Error') });
          }
        },
      });
    }
  };

  const handlePhoneSubmit = (phoneData: IUpdatePhoneForm): void => {
    if (user?.individualIdentity) {
      updateIndividualIdentity({
        variables: {
          updateIndividualIdentityInput: {
            individualIdentityId: user.individualIdentity.id,
            phone: phoneData.phone,
          },
        },
        onCompleted: () => {
          successToast({ title: i18nSettings('Toast.Success') });
        },
        onError: () => {
          errorToast({ title: i18nSettings('Toast.Error') });
        },
      });
    }
  };

  return (
    <Stack spacing="6">
      {backHref && <LinkButton label={i18nGeneral('Back')} route={backHref} />}
      <Heading color="gray.900">{i18nSettings('Title')}</Heading>
      <UpdateProfileForm
        defaultValues={profileDefaults}
        isVerified={isVerified}
        onSubmit={handleProfileSubmit}
      />
      <FiscalAccountFormView />
      <UpdateAddressForm defaultValues={addressDefaults} onSubmit={handleAddressSubmit} />
      <UpdatePasswordForm onSubmit={handlePasswordSubmit} />
      <UpdateEmailForm defaultValues={emailDefaults} onSubmit={handleEmailSubmit} />
      <UpdatePhoneForm defaultValues={phoneDefaults} onSubmit={handlePhoneSubmit} />
      <Box />
    </Stack>
  );
}

export type UserSettingsViewProps = Props;
